import { useState } from 'react';
import { 
  Star, 
  Clock, 
  Ban as Bandage, 
  ArrowUpDown, 
  Trash2, 
  ArrowUp, 
  ArrowDown, 
  Shield,
  ChevronDown,
  ChevronUp,
  Eye
} from 'lucide-react';
import { usePlayerCooldown } from '../../lib/hooks/usePlayerCooldown';
import { InjuryDescriptionModal } from '../Modals/InjuryDescriptionModal';
import { PoolContribution } from './typesMyTeams';
import { useUserStore } from '../../lib/userStore';
import { useEmbedStore } from '../../lib/embedStore';

interface MobilePlayerActionsProps {
  player: any; // MyTeam or Goalie
  isGoalie?: boolean;
  selectedPlayer?: number | null;
  selectedGoalie?: number | null;
  multiSelectMode?: boolean;
  isPlayerSelectedInMultiMode?: (player: any) => boolean;
  language: 'en' | 'fr';
  activeSection: 'MAIN' | 'RESERVES' | 'INJURED' | 'SCRATCH';
  injuredPlayers: Array<{
    playerid: number; 
    description: string; 
    returned_date?: string;
    days_remaining?: number;
    injury_unique_id?: string;
    returned_injury_unique_id?: string;
  }>;
  onRelease: (player: any) => void;
  onToggleReserve: (player: any) => void;
  onSwapSelect: (player: any) => void;
  onSendToInjuredList?: (player: any) => void;
  onReactivateFromInjured?: (player: any) => void;
  onMoveToScratch?: (player: any) => void;
  onRemoveFromScratch?: (player: any) => void;
  onToggleTradingBloc?: (player: any, status: boolean) => void;
  onToggleIntouchable?: (player: any, isIntouchable: boolean) => void;
  onViewDetails?: (player: any) => void;
  partnerLimitedActions?: boolean;
  statsMode?: 'nhl' | 'pool';
  poolStats?: Map<number, PoolContribution>;
}

export function MobilePlayerActions({
  player,
  isGoalie = false,
  selectedPlayer,
  selectedGoalie,
  multiSelectMode = false,
  isPlayerSelectedInMultiMode,
  language,
  activeSection,
  injuredPlayers,
  onRelease,
  onToggleReserve,
  onSwapSelect,
  onSendToInjuredList,
  onReactivateFromInjured,
  onMoveToScratch,
  onRemoveFromScratch,
  onToggleTradingBloc,
  onToggleIntouchable,
  onViewDetails,
  partnerLimitedActions = false,
  statsMode = 'nhl',
  poolStats = new Map()
}: MobilePlayerActionsProps) {
  const { activeCooldowns, startCooldown, getRemainingTime } = usePlayerCooldown();
  const isBpmSession = useUserStore((s) => s.is_bpm_session);
  const isTslhSession = useUserStore((s) => s.is_tslh_session);
  const isEmbed = useEmbedStore((s) => s.isEmbed);
  const limitedActions = isBpmSession || partnerLimitedActions || (isEmbed && !!isTslhSession);
  const [showActions, setShowActions] = useState(false);
  const [showInjuryModal, setShowInjuryModal] = useState(false);

  const playerId = player.playerid;
  const poolContribution = poolStats.get(playerId);
  const isSelected = isGoalie ? selectedGoalie === playerId : selectedPlayer === playerId;
  const isSelectedInMultiMode = multiSelectMode && isPlayerSelectedInMultiMode?.(player);
  const isCooldown = activeCooldowns.has(playerId);

  const displayedGoals = statsMode === 'pool' ? (poolContribution?.pool_goals ?? 0) : (player.goals || 0);
  const displayedAssists = statsMode === 'pool' ? (poolContribution?.pool_assists ?? 0) : (player.assists || 0);
  const displayedPoints = statsMode === 'pool' ? (poolContribution?.pool_points ?? 0) : (player.points || 0);
  const displayedGamesPlayed = statsMode === 'pool' ? (poolContribution?.pool_gamesplayed ?? 0) : (player.gamesplayed || 0);
  const displayedWins = statsMode === 'pool' ? (poolContribution?.pool_wins ?? 0) : (player.wins || 0);

  const injuryInfo = injuredPlayers.find(injury => injury.playerid === playerId);
  // Consider a player as 'injured' for showing the inline injury button only when:
  // - the player exists in injuredPlayers, AND
  // - either we are viewing the INJURED section, OR the injury record has no returned_date (not yet marked returned)
  const isPlayerInjured = !!injuryInfo && (activeSection === 'INJURED' || !injuryInfo.returned_date);
  // IMPORTANT: Show reactivate button whenever we are in the INJURED section, ALWAYS
  // This matches PC behavior where the button is always available in the injured list
  const canReactivate = activeSection === 'INJURED';
  const isDangerousReactivation = injuryInfo?.days_remaining !== undefined && injuryInfo.days_remaining <= 7;
  // For sending to LTIR (placing player on injured list):
  // Show the action if there is an injury record AND either:
  // 1. No returned_date at all (never returned), OR
  // 2. Has returned_date but injury_unique_id doesn't match returned_injury_unique_id (different injury, current one is still active)
  const canSendToInjuredList = !!injuryInfo && (
    !injuryInfo.returned_date || 
    (injuryInfo.injury_unique_id && injuryInfo.injury_unique_id !== injuryInfo.returned_injury_unique_id)
  );

  const handleActionClick = (action: () => void) => {
    if (!isCooldown) {
      startCooldown(playerId);
      action();
    }
  };

  const handleReleaseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleActionClick(() => onRelease(player));
  };

  const handleToggleReserveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleActionClick(() => onToggleReserve(player));
  };

  const handleSwapSelectClick = () => {
    onSwapSelect(player);
  };

  const handleToggleTradingBlocClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleActionClick(() => onToggleTradingBloc?.(player, !player.trading_bloc));
  };

  const handleToggleIntouchableClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleActionClick(() => onToggleIntouchable?.(player, !player.is_intouchable));
  };

  const handleViewDetailsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onViewDetails?.(player);
  };

  const handleMoveToScratchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleActionClick(() => onMoveToScratch?.(player));
  };

  const handleRemoveFromScratchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleActionClick(() => onRemoveFromScratch?.(player));
  };

  const handleSendToInjuredListClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleActionClick(() => onSendToInjuredList?.(player));
  };

  const handleReactivateFromInjuredClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleActionClick(() => onReactivateFromInjured?.(player));
  };

  const handleShowInjuryModal = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setShowInjuryModal(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCompactSalaryValue = (amount: number) => {
    const abs = Math.abs(amount);
    if (abs >= 1_000_000) {
      const millions = amount / 1_000_000;
      const rounded = Math.round(millions * 10) / 10;
      const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
      return `$${text}M`;
    }
    if (abs >= 1_000) {
      const thousands = Math.round(amount / 1_000);
      return `$${thousands}K`;
    }
    return `$${Math.round(amount)}`;
  };

  const formatCompactName = (fullName: string) => {
    const cleaned = (fullName || '').trim();
    if (!cleaned) return '';

    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (parts.length < 2) return cleaned;

    const firstName = parts[0];
    const firstInitial = firstName.charAt(0);

    const joiners = new Set(['St', 'St.', 'De', 'Di', 'Du', 'Del', 'Della', 'Van', 'Von', 'Le', 'La']);
    const last = parts[parts.length - 1];
    const prev = parts[parts.length - 2];
    const lastName =
      parts.length >= 3 && (prev.endsWith('.') || joiners.has(prev))
        ? `${prev}${last}`
        : last;

    return `${firstInitial}.${lastName}`;
  };

  const renderInlineSalary = () => {
    if (!player?.salary) return null;

    const hasRetention =
      (player as any).salary_retention_received_pct && (player as any).salary_retention_received_pct > 0;

    if (hasRetention) {
      const retentionPct = (player as any).salary_retention_received_pct;
      const retainedAmount = (player as any).salary_retention_received_amount || 0;
      const effectiveSalary = player.salary - retainedAmount;

      return (
        <span
          className="badge bg-success fw-bold mobile-salary-badge flex-shrink-0"
          style={{ fontSize: '0.65rem', padding: '0.1rem 0.25rem', whiteSpace: 'nowrap' }}
          title={`${formatCurrency(player.salary)} (-${retentionPct}%)`}
        >
          {formatCompactSalaryValue(effectiveSalary)}
        </span>
      );
    }

    return (
      <span
        className="badge bg-secondary mobile-salary-badge flex-shrink-0"
        style={{ fontSize: '0.65rem', padding: '0.1rem 0.25rem', whiteSpace: 'nowrap' }}
        title={formatCurrency(player.salary)}
      >
        {formatCompactSalaryValue(player.salary)}
      </span>
    );
  };

  // Retourner la carte complète comme dans Club Principal
  return (
    <>
    <div
      className={`py-1 px-1 border-bottom ${
        isSelected
          ? 'border-start border-3 border-primary'
          : multiSelectMode && isSelectedInMultiMode
            ? 'border-start border-3 border-success'
            : ''
      }`}
      style={
        multiSelectMode && isSelectedInMultiMode
          ? {
              backgroundColor: '#d1e7dd',
              boxShadow: '0 0 10px rgba(25, 135, 84, 0.3)',
              border: '2px solid #198754'
            }
          : {}
      }
    >
      <div className="overflow-x-auto overflow-y-hidden myteams-row-scroll">
        <div className="d-flex align-items-center gap-2 flex-nowrap" style={{ minWidth: 'max-content' }}>
        <div className="d-flex align-items-center gap-1 flex-shrink-0">
          <img
            src={`https://assets.nhle.com/mugs/nhl/20232024/${player.teamabbrevs}/${player.playerid}.png`}
            alt={isGoalie ? player.goaliefullname : player.skaterfullname}
            loading="lazy"
            style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer' }}
            className="border"
            onClick={handleViewDetailsClick}
            onError={(e) => {
              e.currentTarget.src = 'https://via.placeholder.com/22x22?text=?';
            }}
          />
          {/* Bouton Actions */}
          <button
            className="btn btn-outline-primary btn-sm d-flex align-items-center"
            onClick={() => setShowActions(!showActions)}
            title={language === 'fr' ? 'Actions' : 'Actions'}
            aria-label={language === 'fr' ? 'Actions' : 'Actions'}
            style={{ fontSize: '0.65rem', padding: '0.1rem 0.25rem' }}
          >
            {showActions ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>

          {/* Bouton Réactiver - visible dans la section INJURED */}
          {activeSection === 'INJURED' && canReactivate && onReactivateFromInjured && (
            <button
              className={`btn btn-sm d-flex align-items-center ${isDangerousReactivation ? 'btn-warning' : 'btn-success'}`}
              onClick={handleReactivateFromInjuredClick}
              disabled={isCooldown}
              title={injuryInfo?.days_remaining !== undefined ? `Réactiver (${injuryInfo?.days_remaining} jours restants)` : 'Réactiver'}
              style={{ fontSize: '0.65rem', padding: '0.1rem 0.25rem' }}
            >
              <Clock size={12} />
              <span className="ms-1">
                {injuryInfo?.days_remaining !== undefined ? `Réactiver (${injuryInfo?.days_remaining}j)` : 'Réactiver'}
              </span>
            </button>
          )}
        </div>

        {/* Infos joueur */}
        <div className="flex-grow-1 min-w-0">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <div className="min-w-0">
              <div className="d-flex align-items-baseline gap-1 min-w-0">
                <div className="fw-bold text-truncate flex-grow-1" style={{ fontSize: '0.8rem', lineHeight: '1.05', cursor: 'pointer' }} onClick={handleViewDetailsClick}>
                  {formatCompactName(isGoalie ? player.goaliefullname : player.skaterfullname)}
                </div>
                {renderInlineSalary()}
              </div>
              <div className="d-flex align-items-center gap-1 flex-wrap mt-1">
                {player.selection_percentage !== undefined && (
                  <span
                    className="badge bg-info text-dark flex-shrink-0"
                    style={{ fontSize: '0.65rem', padding: '0.1rem 0.3rem', whiteSpace: 'nowrap' }}
                  >
                    {Math.round(Number(player.selection_percentage))} % {language === 'fr' ? 'sél.' : 'sel.'}
                  </span>
                )}
              </div>
              <div className="d-flex align-items-center gap-1 flex-wrap">
                <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                  {player.teamabbrevs}
                </span>
                {isPlayerInjured && injuryInfo && (
                  <button
                    className="btn btn-outline-danger btn-sm d-flex align-items-center"
                    onClick={handleShowInjuryModal}
                    title={`Blessure: ${injuryInfo.description || 'Non spécifiée'}`}
                    style={{ fontSize: '0.65rem', padding: '0.05rem 0.25rem' }}
                  >
                    <Bandage size={12} />
                    <span className="ms-1">Info</span>
                  </button>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="d-flex align-items-center gap-1 flex-shrink-0 text-nowrap">
                {isGoalie ? (
                  <>
                    <span className="fw-bold text-secondary" style={{ fontSize: '0.78rem' }}>
                      V {displayedWins}
                    </span>
                    <span className="fw-bold text-danger" style={{ fontSize: '0.78rem' }}>
                      L {player.losses || 0}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="fw-bold text-secondary" style={{ fontSize: '0.78rem' }}>
                      PTS {displayedPoints}
                    </span>
                    <span className="fw-bold text-success" style={{ fontSize: '0.78rem' }}>
                      G {displayedGoals}
                    </span>
                    <span className="fw-bold text-warning" style={{ fontSize: '0.78rem' }}>
                      A {displayedAssists}
                    </span>
                  </>
                )}
                <span className="fw-bold text-secondary" style={{ fontSize: '0.78rem' }}>
                  GP {displayedGamesPlayed}
                </span>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Actions étendues */}
      {showActions && (
        <div className="mt-1 d-flex flex-wrap gap-1">
          {/* Voir détails */}
          {onViewDetails && (
            <button
              className="btn btn-outline-info btn-sm d-flex align-items-center"
              onClick={handleViewDetailsClick}
              style={{ fontSize: '0.7rem', padding: '0.15rem 0.35rem' }}
            >
              <Eye size={12} />
              <span className="ms-1">{language === 'fr' ? 'Détails' : 'Details'}</span>
            </button>
          )}

          {/* Swap */}
          {!limitedActions && <button
            className={`btn btn-sm ${isSelected || isSelectedInMultiMode ? 'btn-success' : 'btn-outline-secondary'}`}
            onClick={handleSwapSelectClick}
            title={language === 'fr' ? 'Sélectionner pour échange' : 'Select for swap'}
            style={{ fontSize: '0.7rem', padding: '0.15rem 0.35rem' }}
          >
            <ArrowUpDown size={12} />
            <span className="ms-1">Swap</span>
          </button>}

          {/* Trading Bloc */}
          {!limitedActions && onToggleTradingBloc && (activeSection === 'MAIN' || activeSection === 'RESERVES') && (
            <div className="position-relative">
              <button
                className={`btn btn-sm ${player.trading_bloc === true ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={handleToggleTradingBlocClick}
                disabled={isCooldown}
                title={player.trading_bloc === true ? 'Retirer du Trading Bloc' : 'Ajouter au Trading Bloc'}
                style={{ fontSize: '0.7rem', padding: '0.15rem 0.35rem' }}
              >
                <Star size={12} />
                <span className="ms-1">Trading</span>
              </button>
              {isCooldown && (
                <div className="cooldown-overlay">
                  {Math.ceil(getRemainingTime(playerId) / 1000)}s
                </div>
              )}
            </div>
          )}

          {/* Intouchable */}
          {!limitedActions && onToggleIntouchable && (activeSection === 'MAIN' || activeSection === 'RESERVES') && (
            <div className="position-relative">
              <button
                className={`btn btn-sm ${player.is_intouchable === true ? 'btn-info' : 'btn-outline-info'}`}
                onClick={handleToggleIntouchableClick}
                disabled={isCooldown}
                title={player.is_intouchable === true ? 'Retirer des Intouchables' : 'Marquer comme Intouchable'}
                style={{ fontSize: '0.7rem', padding: '0.15rem 0.35rem' }}
              >
                <Shield size={12} />
                <span className="ms-1">Protégé</span>
              </button>
              {isCooldown && (
                <div className="cooldown-overlay">
                  {Math.ceil(getRemainingTime(playerId) / 1000)}s
                </div>
              )}
            </div>
          )}

          {/* Actions selon la section */}
          {activeSection !== 'INJURED' && onSendToInjuredList && canSendToInjuredList && (
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={handleSendToInjuredListClick}
              disabled={isCooldown}
              title="Placer sur LTIR"
              style={{ fontSize: '0.7rem', padding: '0.15rem 0.35rem' }}
            >
              <Bandage size={12} />
              <span className="ms-1">LTIR</span>
            </button>
          )}
          {!limitedActions && activeSection !== 'INJURED' && (
            <>
              <button
                className={`btn btn-sm ${player.isnhl ? 'btn-outline-info' : 'btn-outline-success'}`}
                onClick={handleToggleReserveClick}
                disabled={isCooldown}
                title={player.isnhl
                  ? (language === 'fr' ? 'Rétrograder aux réserves' : 'Demote to reserves')
                  : (language === 'fr' ? 'Promouvoir au club principal' : 'Promote to main club')
                }
                style={{ fontSize: '0.7rem', padding: '0.15rem 0.35rem' }}
              >
                {player.isnhl ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                <span className="ms-1">{player.isnhl ? 'Rétrograder' : 'Promouvoir'}</span>
              </button>

              {activeSection === 'MAIN' && onMoveToScratch && (
                <button
                  className="btn btn-outline-warning btn-sm"
                  onClick={handleMoveToScratchClick}
                  disabled={isCooldown}
                  title="Déplacer vers scratch"
                  style={{ fontSize: '0.7rem', padding: '0.15rem 0.35rem' }}
                >
                  📋
                  <span className="ms-1">Scratch</span>
                </button>
              )}

              {activeSection === 'SCRATCH' && onRemoveFromScratch && (
                <button
                  className="btn btn-outline-success btn-sm"
                  onClick={handleRemoveFromScratchClick}
                  disabled={isCooldown}
                  title="Retirer des scratch"
                  style={{ fontSize: '0.7rem', padding: '0.15rem 0.35rem' }}
                >
                  ✅
                  <span className="ms-1">Activer</span>
                </button>
              )}
            </>
          )}

          <button
            className="btn btn-outline-danger btn-sm"
            onClick={handleReleaseClick}
            disabled={isCooldown}
            title={language === 'fr' ? 'Libérer le joueur' : 'Release player'}
            style={{ fontSize: '0.7rem', padding: '0.15rem 0.35rem' }}
          >
            <Trash2 size={12} />
          </button>

          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setShowActions(false)}
            title="Masquer les actions"
            style={{ fontSize: '0.7rem', padding: '0.15rem 0.35rem' }}
          >
            ×
          </button>
        </div>
      )}
    </div>
      <InjuryDescriptionModal
        show={showInjuryModal}
        onClose={() => setShowInjuryModal(false)}
        playerName={isGoalie ? player.goaliefullname : player.skaterfullname}
        description={injuryInfo?.description || ''}
        language={language}
        daysRemaining={injuryInfo?.days_remaining}
        isReturned={!!injuryInfo?.returned_date}
      />
    </>
  );
}
